import { Clock, Mail, MapPin, Phone } from "lucide-react";
import PageHero from "@/components/PageHero";
import { Eyebrow, FadeUp } from "@/components/Reveal";
import EnquiryForm from "@/components/EnquiryForm";
import Faq from "@/components/Faq";
import { CONTACT, FAQS } from "@/data/site";

export default function Contact() {
    return (
        <>
            <PageHero
                crumb="Contact Us"
                eyebrow="Sahibabad · Ghaziabad · Uttar Pradesh"
                titleLines={["Contact", "Us."]}
                lead="Share your PCB requirement, call us or send your Gerber data — our team will respond promptly."
            />

            <section className="bg-white py-20 md:py-28" data-testid="contact-page">
                <div className="mx-auto grid max-w-7xl gap-14 px-4 sm:px-6 lg:grid-cols-5 lg:gap-16 lg:px-8">
                    {/* Contact details */}
                    <div className="lg:col-span-2">
                        <FadeUp>
                            <Eyebrow>Contact Details</Eyebrow>
                            <h2 className="mt-5 text-3xl font-bold tracking-tight text-navy md:text-4xl">
                                Talk to our team
                            </h2>
                            <p className="mt-5 text-[14.5px] leading-relaxed text-slate-600">
                                Call, email or send the enquiry form — for faster quotations, attach
                                your Gerber data and mention board type and quantity.
                            </p>
                        </FadeUp>

                        <div className="mt-10 space-y-px bg-slate-200" data-testid="contact-details">
                            <FadeUp className="flex gap-4 bg-white p-5">
                                <MapPin className="mt-0.5 h-5 w-5 shrink-0 text-brand-red" strokeWidth={1.75} />
                                <div>
                                    <p className="font-mono text-[10.5px] uppercase tracking-[0.18em] text-slate-500">Address</p>
                                    <p className="mt-1.5 text-[14.5px] leading-relaxed text-navy">{CONTACT.address}</p>
                                </div>
                            </FadeUp>
                            <FadeUp delay={0.05} className="flex gap-4 bg-white p-5">
                                <Phone className="mt-0.5 h-5 w-5 shrink-0 text-brand-red" strokeWidth={1.75} />
                                <div>
                                    <p className="font-mono text-[10.5px] uppercase tracking-[0.18em] text-slate-500">Phone Numbers</p>
                                    <a href={CONTACT.phone1Href} data-testid="contact-phone-1" className="mt-1.5 block text-[14.5px] font-medium text-navy transition-colors hover:text-brand-red">
                                        {CONTACT.phone1}
                                    </a>
                                    <a href={CONTACT.phone2Href} data-testid="contact-phone-2" className="block text-[14.5px] font-medium text-navy transition-colors hover:text-brand-red">
                                        {CONTACT.phone2}
                                    </a>
                                </div>
                            </FadeUp>
                            <FadeUp delay={0.1} className="flex gap-4 bg-white p-5">
                                <Mail className="mt-0.5 h-5 w-5 shrink-0 text-brand-red" strokeWidth={1.75} />
                                <div>
                                    <p className="font-mono text-[10.5px] uppercase tracking-[0.18em] text-slate-500">Email</p>
                                    <a href={`mailto:${CONTACT.email}`} data-testid="contact-email" className="mt-1.5 block break-all text-[14.5px] font-medium text-navy transition-colors hover:text-brand-red">
                                        {CONTACT.email}
                                    </a>
                                </div>
                            </FadeUp>
                            <FadeUp delay={0.15} className="flex gap-4 bg-white p-5">
                                <Clock className="mt-0.5 h-5 w-5 shrink-0 text-brand-red" strokeWidth={1.75} />
                                <div>
                                    <p className="font-mono text-[10.5px] uppercase tracking-[0.18em] text-slate-500">Business Hours</p>
                                    <p className="mt-1.5 text-[14.5px] text-navy">{CONTACT.hours}</p>
                                </div>
                            </FadeUp>
                        </div>
                    </div>

                    {/* Form */}
                    <div className="lg:col-span-3">
                        <FadeUp className="border border-slate-200 bg-white p-6 sm:p-10">
                            <h2 className="text-2xl font-bold tracking-tight text-navy">Send Your Enquiry</h2>
                            <p className="mt-2 text-[13.5px] text-slate-500">
                                Fields marked * are required. Gerber data can be attached below.
                            </p>
                            <div className="mt-8">
                                <EnquiryForm />
                            </div>
                        </FadeUp>
                    </div>
                </div>
            </section>

            {/* FAQ */}
            <section className="border-t border-slate-200 bg-slate-50 py-20 md:py-28" data-testid="contact-faq-section">
                <div className="mx-auto grid max-w-7xl gap-14 px-4 sm:px-6 lg:grid-cols-5 lg:gap-16 lg:px-8">
                    <div className="lg:col-span-2">
                        <FadeUp>
                            <Eyebrow>Frequently Asked</Eyebrow>
                            <h2 className="mt-5 text-3xl font-bold tracking-tight text-navy md:text-4xl">
                                Common questions from buyers
                            </h2>
                            <p className="mt-5 text-[14.5px] leading-relaxed text-slate-600">
                                Everything you need before sending your first enquiry. If your question
                                is not answered here, call us or drop it in the message field of the
                                enquiry form.
                            </p>
                        </FadeUp>
                    </div>
                    <div className="lg:col-span-3">
                        <FadeUp>
                            <Faq items={FAQS} testId="contact-faq" />
                        </FadeUp>
                    </div>
                </div>
            </section>

            <section className="border-t border-slate-200 bg-white py-16 md:py-20" data-testid="contact-map-section">
                <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                    <FadeUp>
                        <Eyebrow>Find Us</Eyebrow>
                        <h2 className="mt-4 text-2xl font-bold tracking-tight text-navy md:text-3xl">
                            Our Location
                        </h2>
                        <p className="mt-3 text-[14px] leading-relaxed text-slate-500">
                            {CONTACT.address}
                        </p>
                    </FadeUp>
                    <FadeUp className="mt-8 border border-slate-200 bg-white p-2">
                        <iframe
                            title="Unimicron Technology India location — H-60, Durga Industrial Area, Sahibabad, Ghaziabad, Uttar Pradesh"
                            src="https://maps.google.com/maps?q=H-60%2C%20Durga%20Industrial%20Area%2C%20Sahibabad%2C%20Ghaziabad%2C%20Uttar%20Pradesh&t=&z=14&ie=UTF8&iwloc=&output=embed"
                            className="h-[360px] w-full md:h-[420px]"
                            loading="lazy"
                            referrerPolicy="no-referrer-when-downgrade"
                            data-testid="contact-map-iframe"
                        />
                    </FadeUp>
                </div>
            </section>
        </>
    );
}
